import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';
import { createApiService } from '@/services/api';
import { Users, UserCheck, Activity, RefreshCw, Download, Clock } from 'lucide-react';
import { BrandedLoading } from '@/components/common/BrandedLoading';
import { Button } from '@/components/ui/Button';
import { exportToCSV } from '@/utils/csvExport';

const userProfileApi = createApiService(
  import.meta.env.VITE_USER_PROFILE_API_BASE_URL || '/api/user-profile'
);

const analyticsApi = createApiService(
  import.meta.env.VITE_ANALYTICS_API_BASE_URL || '/api/analytics'
);

interface RecentActivityEntry {
  id: string;
  userId: string;
  eventType: string;
  eventName: string;
  source: string;
  timestamp: string;
}

interface UserReportState {
  totalUsers: number;
  dau: number;
  wau: number;
  mau: number;
  onboardingStarted: number;
  onboardingCompleted: number;
  onboardingCompletionRate: number;
  topUsers: { email: string; activityCount: number }[];
}

const EVENT_TYPE_OPTIONS = [
  'ALL',
  'USER_LOGIN',
  'USER_LOGOUT',
  'AI_QUERY_SUBMITTED',
  'AI_RESPONSE_RECEIVED',
  'PAGE_VIEW',
  'FEATURE_COMPLETED',
  'FEATURE_ACCESSED',
  'ERROR_OCCURRED',
];

function formatActivityTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMin / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export const UserReportPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState<RecentActivityEntry[]>([]);
  const [activityFilter, setActivityFilter] = useState('ALL');
  const [activityLoading, setActivityLoading] = useState(false);
  const [data, setData] = useState<UserReportState>({
    totalUsers: 0,
    dau: 0,
    wau: 0,
    mau: 0,
    onboardingStarted: 0,
    onboardingCompleted: 0,
    onboardingCompletionRate: 0,
    topUsers: [],
  });
  const { toast } = useToast();

  const fetchData = async () => {
    try {
      setIsLoading(true);

      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] + 'T00:00:00';
      const endDate = now.toISOString().split('T')[0] + 'T00:00:00';

      const [userStats, activityStats] = await Promise.all([
        // Get total user count from profile listing (page 0, size 1 — just need totalElements)
        userProfileApi
          .get<Record<string, unknown>>(`/api/v1/profiles?page=0&size=1`)
          .catch(() => ({})),
        // Get top users by cost (admin-level endpoint)
        analyticsApi
          .get<Record<string, unknown>>(
            `/api/v1/usage-metrics/users/top-by-cost?startDate=${thirtyDaysAgo}&endDate=${endDate}&limit=10`
          )
          .catch(() => ({})),
      ]);

      const usObj = (userStats ?? {}) as Record<string, unknown>;
      const aObj = (activityStats ?? {}) as Record<string, unknown>;

      // Profile list returns paginated: { content: [...], totalElements: N }
      const dataObj = (usObj.data ?? usObj) as Record<string, unknown>;
      const totalUsers = Number(dataObj.totalElements ?? dataObj.totalUsers ?? dataObj.total ?? 0);
      const onboardingStarted = Number(usObj.onboardingStarted ?? 0);
      const onboardingCompleted = Number(usObj.onboardingCompleted ?? 0);
      const onboardingCompletionRate =
        onboardingStarted > 0 ? onboardingCompleted / onboardingStarted : 0;

      // Active user metrics from analytics service
      const dau = Number(aObj.dailyActiveUsers ?? aObj.dau ?? 0);
      const wau = Number(aObj.weeklyActiveUsers ?? aObj.wau ?? 0);
      const mau = Number(aObj.monthlyActiveUsers ?? aObj.mau ?? 0);

      // Top users by engagement
      const topUsers = ((aObj.topUsers ?? []) as { email: string; activityCount: number }[]).slice(
        0,
        10
      );

      setData({
        totalUsers,
        dau,
        wau,
        mau,
        onboardingStarted,
        onboardingCompleted,
        onboardingCompletionRate,
        topUsers,
      });
    } catch (error) {
      console.error('Error fetching user report:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load user report',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRecentActivities = useCallback(async () => {
    setActivityLoading(true);
    try {
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const startDate = sevenDaysAgo.toISOString().split('.')[0];
      const endDate = now.toISOString().split('.')[0];

      // Use the query endpoint to get recent activities across all users
      const resp = await analyticsApi.post<{ content?: RecentActivityEntry[] }>(
        `/api/v1/user-activities/query?page=0&size=50&sort=timestamp,desc`,
        {
          startDate,
          endDate,
          ...(activityFilter !== 'ALL' ? { eventTypes: [activityFilter] } : {}),
        }
      );
      const data = (resp ?? {}) as Record<string, unknown>;
      const content = (data.content ?? data.data ?? data) as RecentActivityEntry[] | undefined;
      setRecentActivities(Array.isArray(content) ? content : []);
    } catch {
      setRecentActivities([]);
    } finally {
      setActivityLoading(false);
    }
  }, [activityFilter]);

  useEffect(() => {
    fetchData();
    fetchRecentActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRecentActivities();
  }, [fetchRecentActivities]);

  const handleExportCSV = () => {
    const rows = [
      { Metric: 'Total Users', Value: data.totalUsers },
      { Metric: 'DAU', Value: data.dau },
      { Metric: 'WAU', Value: data.wau },
      { Metric: 'MAU', Value: data.mau },
      { Metric: 'Onboarding Started', Value: data.onboardingStarted },
      { Metric: 'Onboarding Completed', Value: data.onboardingCompleted },
      {
        Metric: 'Onboarding Completion Rate',
        Value: `${(data.onboardingCompletionRate * 100).toFixed(1)}%`,
      },
      ...data.topUsers.map((u) => ({
        Metric: `Top User: ${u.email}`,
        Value: u.activityCount,
      })),
    ];
    exportToCSV(rows, 'user-report.csv');
  };

  const kpiCards = useMemo(
    () => [
      {
        title: 'Total Users',
        value: data.totalUsers,
        icon: <Users className="h-4 w-4 text-blue-600" />,
        color: 'text-blue-600',
      },
      {
        title: 'DAU',
        value: data.dau,
        description: 'Last 24 hours',
        icon: <Activity className="h-4 w-4 text-green-600" />,
        color: 'text-green-600',
      },
      {
        title: 'WAU',
        value: data.wau,
        description: 'Last 7 days',
        icon: <Activity className="h-4 w-4 text-purple-600" />,
        color: 'text-purple-600',
      },
      {
        title: 'MAU',
        value: data.mau,
        description: 'Last 30 days',
        icon: <Activity className="h-4 w-4 text-orange-600" />,
        color: 'text-orange-600',
      },
      {
        title: 'Onboarding Rate',
        value: `${(data.onboardingCompletionRate * 100).toFixed(1)}%`,
        description: `${data.onboardingCompleted} / ${data.onboardingStarted}`,
        icon: <UserCheck className="h-4 w-4 text-indigo-600" />,
        color: 'text-indigo-600',
      },
    ],
    [data]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Users className="h-8 w-8" />
            User Report
          </h1>
          <p className="text-muted-foreground mt-2">
            User activity, engagement, and onboarding metrics
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <BrandedLoading message="Loading user report…" iconSize="sm" />
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            {kpiCards.map((kpi) => (
              <Card key={kpi.title}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{kpi.title}</CardTitle>
                  {kpi.icon}
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</div>
                  {'description' in kpi && kpi.description && (
                    <p className="text-xs text-muted-foreground mt-1">{kpi.description}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Top Users by Engagement */}
          <Card>
            <CardHeader>
              <CardTitle>Top Users by Engagement</CardTitle>
              <CardDescription>Users with the highest activity count</CardDescription>
            </CardHeader>
            <CardContent>
              {data.topUsers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium">#</th>
                        <th className="text-left py-2 px-3 font-medium">User</th>
                        <th className="text-right py-2 px-3 font-medium">Activity Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topUsers.map((user, idx) => (
                        <tr key={user.email} className="border-b hover:bg-muted/50">
                          <td className="py-2 px-3 text-muted-foreground">{idx + 1}</td>
                          <td className="py-2 px-3">{user.email}</td>
                          <td className="py-2 px-3 text-right font-mono">{user.activityCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No engagement data available. Top users will appear here once the analytics service provides user activity data.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Onboarding Funnel */}
          <Card>
            <CardHeader>
              <CardTitle>Onboarding Funnel</CardTitle>
              <CardDescription>Started vs Completed onboarding</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Started</span>
                  <div className="flex items-center gap-4">
                    <div className="w-48 bg-secondary rounded-full h-3">
                      <div className="bg-blue-500 h-3 rounded-full" style={{ width: '100%' }} />
                    </div>
                    <span className="text-sm font-medium w-16 text-right">
                      {data.onboardingStarted}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Completed</span>
                  <div className="flex items-center gap-4">
                    <div className="w-48 bg-secondary rounded-full h-3">
                      <div
                        className="bg-green-500 h-3 rounded-full"
                        style={{
                          width: `${data.onboardingStarted > 0 ? (data.onboardingCompleted / data.onboardingStarted) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium w-16 text-right">
                      {data.onboardingCompleted}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Platform Activity */}
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Recent Platform Activity
                  </CardTitle>
                  <CardDescription>Last 50 activities across all users (past 7 days)</CardDescription>
                </div>
                <select
                  className="text-sm border rounded-md px-2 py-1 bg-background"
                  value={activityFilter}
                  onChange={(e) => setActivityFilter(e.target.value)}
                >
                  {EVENT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === 'ALL' ? 'All Events' : opt.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
            </CardHeader>
            <CardContent>
              {activityLoading ? (
                <div className="flex justify-center py-8">
                  <BrandedLoading message="Loading activities…" iconSize="sm" />
                </div>
              ) : recentActivities.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium">User</th>
                        <th className="text-left py-2 px-3 font-medium">Event</th>
                        <th className="text-left py-2 px-3 font-medium">Source</th>
                        <th className="text-right py-2 px-3 font-medium">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentActivities.map((a) => (
                        <tr key={a.id} className="border-b hover:bg-muted/50">
                          <td className="py-2 px-3 font-mono text-xs truncate max-w-[160px]">{a.userId}</td>
                          <td className="py-2 px-3">
                            <span className="inline-flex items-center gap-1.5">
                              <span className="text-xs px-1.5 py-0.5 rounded bg-muted font-medium">
                                {(a.eventType ?? '').replace(/_/g, ' ')}
                              </span>
                            </span>
                          </td>
                          <td className="py-2 px-3 text-muted-foreground text-xs">{a.source ?? '-'}</td>
                          <td className="py-2 px-3 text-right text-muted-foreground text-xs whitespace-nowrap">
                            {a.timestamp ? formatActivityTime(a.timestamp) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No recent activity found. Activities will appear here once the analytics service collects user events.
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default UserReportPage;
